// @ts-strict-ignore
export class Iterator {
  private _pos: number = 0;
  private length: number = 0;
  private _text: string = '';

  constructor(text: string) {
    this._text = text;
    this.length = text.length;
  }

  public peek(num: number = 0): string {
    num = num || 0;
    if (this._pos + num >= this.length) {
      return null;
    }
    return this._text.charAt(this._pos + num);
  }

  public next(inc: number = 1): string {
    inc = inc || 1;
    if (this._pos >= this.length) {
      return null;
    }
    return this._text.charAt((this._pos += inc) - inc);
  }

  public pos(): number {
    return this._pos;
  }
}

export class PropertyParser {
  private static readonly rWhitespace: RegExp = /\s/;

  private static renderChild: any = {
    'escaped-value': (child, text) => {
      const type = text.charAt(child.start + 1);

      if (type === 't') {
        return '\t';
      }
      if (type === 'r') {
        return '\r';
      }
      if (type === 'n') {
        return '\n';
      }
      if (type === 'f') {
        return '\f';
      }
      if (type !== 'u') {
        return type;
      }

      return String.fromCharCode(parseInt(text.substr(child.start + 2, 4), 16));
    },
    'line-break': (child, text) => {
      return '';
    },
  };

  private static isWhitespace(chr: string): boolean {
    return this.rWhitespace.test(chr);
  }

  private static consumeWhiteSpace(iter: Iterator): any {
    const start = iter.pos();

    while (this.isWhitespace(iter.peek())) {
      iter.next(undefined);
    }

    return { type: 'whitespace', start: start, end: iter.pos() };
  }

  private static startsComment(chr: string): boolean {
    return chr === '!' || chr === '#';
  }
  private static isEOL(chr: string): boolean {
    return chr == null || chr === '\n' || chr === '\r';
  }

  private static consumeComment(iter: Iterator): any {
    const start = iter.pos();

    while (!this.isEOL(iter.peek())) {
      iter.next(undefined);
    }

    return { type: 'comment', start: start, end: iter.pos() };
  }

  private static startsKeyVal(chr: string): boolean {
    return !this.isWhitespace(chr) && !this.startsComment(chr);
  }

  private static startsSeparator(chr: string): boolean {
    return chr === '=' || chr === ':' || this.isWhitespace(chr);
  }

  private static startsEscapedVal(chr: string): boolean {
    return chr === '\\';
  }

  private static consumeEscapedVal(iter: Iterator): any {
    const start = iter.pos();

    iter.next(undefined); // move past "\"
    const curChar = iter.next(undefined);
    if (curChar === 'u') {
      // encoded unicode char
      iter.next(4); // Read in the 4 hex values
    }

    return { type: 'escaped-value', start: start, end: iter.pos() };
  }

  private static consumeKey(iter: Iterator): any {
    const start = iter.pos(),
      children = [];

    let curChar;
    while ((curChar = iter.peek()) !== null) {
      if (this.startsSeparator(curChar)) {
        break;
      }
      if (this.startsEscapedVal(curChar)) {
        children.push(this.consumeEscapedVal(iter));
        continue;
      }

      iter.next(undefined);
    }

    return { type: 'key', start: start, end: iter.pos(), children: children };
  }

  private static consumeKeyValSeparator(iter: Iterator): any {
    const start = iter.pos();

    let seenHardSep = false;
    let curChar;
    while ((curChar = iter.peek()) !== null) {
      if (this.isEOL(curChar)) {
        break;
      }

      if (this.isWhitespace(curChar)) {
        iter.next(undefined);
        continue;
      }

      if (seenHardSep) {
        break;
      }

      seenHardSep = curChar === ':' || curChar === '=';
      if (seenHardSep) {
        iter.next(undefined);
        continue;
      }

      break; // curChar is a non-separtor char
    }

    return { type: 'key-value-separator', start: start, end: iter.pos() };
  }

  private static startsLineBreak(iter: Iterator): boolean {
    return iter.peek() === '\\' && this.isEOL(iter.peek(1));
  }

  private static consumeLineBreak(iter: Iterator): any {
    const start = iter.pos();

    iter.next(undefined); // consume \
    if (iter.peek() === '\r') {
      iter.next(undefined);
    }
    iter.next(undefined); // consume \n

    let curChar;
    while ((curChar = iter.peek()) !== null) {
      if (this.isEOL(curChar)) {
        break;
      }
      if (!this.isWhitespace(curChar)) {
        break;
      }

      iter.next(undefined);
    }

    return { type: 'line-break', start: start, end: iter.pos() };
  }

  private static consumeVal(iter: Iterator): any {
    const start = iter.pos(),
      children = [];

    let curChar;
    while ((curChar = iter.peek()) !== null) {
      if (this.startsLineBreak(iter)) {
        children.push(this.consumeLineBreak(iter));
        continue;
      }
      if (this.startsEscapedVal(curChar)) {
        children.push(this.consumeEscapedVal(iter));
        continue;
      }
      if (this.isEOL(curChar)) {
        break;
      }

      iter.next(undefined);
    }

    return { type: 'value', start: start, end: iter.pos(), children: children };
  }

  private static consumeKeyVal(iter: Iterator): any {
    return {
      type: 'key-value',
      start: iter.pos(),
      children: [this.consumeKey(iter), this.consumeKeyValSeparator(iter), this.consumeVal(iter)],
      end: iter.pos(),
    };
  }

  private static rangeToBuffer(range: any, text: string): string[] {
    let start = range.start;
    const buffer = [];

    for (let i = 0; i < range.children.length; i++) {
      const child = range.children[i];

      buffer.push(text.substring(start, child.start));
      buffer.push(this.renderChild[child.type](child, text));
      start = child.end;
    }
    buffer.push(text.substring(start, range.end));

    return buffer;
  }

  private static rangesToObject(ranges: any[], text: string): Object {
    const obj = Object.create(null); // Creates to a true hash map

    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i];

      if (range.type !== 'key-value') {
        continue;
      }

      const key = this.rangeToBuffer(range.children[0], text).join('');
      const val = this.rangeToBuffer(range.children[2], text).join('');
      obj[key] = val;
    }

    return obj;
  }

  private static stringToRanges(text: string): any[] {
    const iter = new Iterator(text),
      ranges = [];

    let curChar;
    while ((curChar = iter.peek()) !== null) {
      if (this.isWhitespace(curChar)) {
        ranges.push(this.consumeWhiteSpace(iter));
        continue;
      }
      if (this.startsComment(curChar)) {
        ranges.push(this.consumeComment(iter));
        continue;
      }
      if (this.startsKeyVal(curChar)) {
        ranges.push(this.consumeKeyVal(iter));
        continue;
      }

      throw Error("Something crazy happened. text: '" + text + "'; curChar: '" + curChar + "'");
    }

    return ranges;
  }

  public static parse(text: string): any {
    text = text.toString();
    const ranges = this.stringToRanges(text);
    return this.rangesToObject(ranges, text);
  }
}
